import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, ShieldCheck, Globe2, Award, Send, BadgeCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, IMAGES, COMMODITIES, COMMODITIES_FR, LOGO_URL } from "@/lib/constants";
import { localizeProducts } from "@/data/products";
import { useLanguage } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import OperationsSlider from "@/components/OperationsSlider";

const HERO_IMAGES = [IMAGES.hero, IMAGES.cathodesBundles, IMAGES.shipping];

const Counter = ({ value, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2.2,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);
  return (
    <span ref={ref} data-testid="trust-counter-value">
      {display}
      {suffix}
    </span>
  );
};

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const { t, lang } = useLanguage();
  const products = localizeProducts(lang);
  const commodityOptions = lang === "fr" ? COMMODITIES_FR : COMMODITIES;

  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(id);
  }, []);

  const [lead, setLead] = useState({ company_name: "", email: "", commodity: "", message: "" });
  const [sending, setSending] = useState(false);
  const [leadRef, setLeadRef] = useState(null);

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/inquiries`, {
        inquiry_type: "lead",
        company_name: lead.company_name,
        email: lead.email,
        commodity: lead.commodity || null,
        message: lead.message || null,
      });
      setLeadRef(data.reference);
      toast.success(`${t("home.lead.toastOk")} ${data.reference}`);
      setLead({ company_name: "", email: "", commodity: "", message: "" });
    } catch (err) {
      toast.error(t("home.lead.toastErr"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="home-page" key={lang}>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-slider">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <AnimatePresence mode="sync">
            <motion.img
              key={heroIdx}
              src={HERO_IMAGES[heroIdx]}
              alt="Ore Source Mining operations"
              initial={{ opacity: 0, scale: 1.07 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6, ease: "easeOut" }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a192f] via-[#0a192f]/80 to-[#0a192f]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f] via-transparent to-[#0a192f]/60" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mb-8 inline-flex items-center gap-3 border border-[#4a6cf7]/30 bg-[#0a192f]/60 px-4 py-2 backdrop-blur-md"
          >
            <img src={LOGO_URL} alt="" className="h-8 w-8 rounded-full bg-white p-[3px] ring-1 ring-[#4a6cf7]/60" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-[#9db4ff] sm:text-xs">
              {t("home.hero.badge")}
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display max-w-4xl text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            <HeroLine delay={0.35}>{t("home.hero.l1")}</HeroLine>
            <HeroLine delay={0.5}>{t("home.hero.l2")}</HeroLine>
            <HeroLine delay={0.65}>
              <span className="gold-text">{t("home.hero.l3")}</span>
            </HeroLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            {t("home.hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-sco"
              className="group relative flex items-center justify-center gap-3 overflow-hidden border border-[#4a6cf7] bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6] px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-white transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <span className="shine-sweep absolute inset-y-0 w-16 bg-white/30" />
              {t("home.hero.cta1")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/products"
              data-testid="hero-cta-specs"
              className="flex items-center justify-center gap-3 border border-slate-400/40 px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.2em] text-slate-100 backdrop-blur-sm transition-colors duration-300 hover:border-[#4a6cf7] hover:text-[#9db4ff]"
            >
              {t("home.hero.cta2")}
            </Link>
          </motion.div>
        </motion.div>

        <div className="absolute bottom-8 right-5 z-10 flex gap-2 lg:right-8">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Hero slide ${i + 1}`}
              onClick={() => setHeroIdx(i)}
              className={`h-1.5 transition-all duration-500 ${
                heroIdx === i ? "w-10 bg-[#4a6cf7]" : "w-4 bg-white/25 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </section>

      <Marquee />

      {/* TRUST COUNTERS */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="trust-metrics-section">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
            {t("home.trust.kicker")}
          </p>
          <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
            {t("home.trust.title")}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-3">
          <Reveal className="bg-[#0d223f] p-10" delay={0.05}>
            <div className="flex items-center gap-3 text-[#4a6cf7]">
              <Award className="h-6 w-6" />
              <p className="font-display text-4xl font-bold text-white sm:text-5xl">
                <Counter value={99.99} decimals={2} suffix="%" />
              </p>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("home.trust.m1")}</p>
          </Reveal>
          <Reveal className="bg-[#0d223f] p-10" delay={0.15}>
            <div className="flex items-center gap-3 text-[#4a6cf7]">
              <ShieldCheck className="h-6 w-6" />
              <p className="font-display text-4xl font-bold text-white sm:text-5xl">Tier-1</p>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("home.trust.m2")}</p>
          </Reveal>
          <Reveal className="bg-[#0d223f] p-10" delay={0.25}>
            <div className="flex items-center gap-3 text-[#4a6cf7]">
              <Globe2 className="h-6 w-6" />
              <p className="font-display text-4xl font-bold text-white sm:text-5xl">
                <Counter value={3} suffix="+" />
              </p>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("home.trust.m3")}</p>
          </Reveal>
        </div>
      </section>

      {/* REGISTRATION CREDENTIALS STRIP */}
      <section className="border-y border-[#4a6cf7]/20 bg-[#0d223f]" data-testid="credentials-strip">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-4 px-5 py-6 lg:px-8">
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-[#4a6cf7]" />
            <span className="font-display text-xs font-semibold uppercase tracking-[0.25em] text-white">
              {t("home.creds.kicker")}
            </span>
          </div>
          {t("home.creds.items").map((c) => (
            <span
              key={c}
              className="font-mono2 border border-white/10 bg-[#0a192f] px-3 py-1.5 text-[11px] tracking-wider text-slate-300"
            >
              {c}
            </span>
          ))}
        </div>
      </section>

      {/* WAREHOUSE / TRUST IMAGE */}
      <section className="border-b border-[#4a6cf7]/15 bg-[#0d223f]">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="group relative overflow-hidden border border-[#4a6cf7]/25">
              <img
                src={IMAGES.cathodesWarehouse}
                alt="Stacked electrolytic copper cathodes in warehouse"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f]/80 via-transparent to-transparent" />
              <p className="font-mono2 absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-[#9db4ff]">
                {t("home.warehouse.caption")}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
              {t("home.warehouse.kicker")}
            </p>
            <h3 className="font-display mt-4 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              {t("home.warehouse.title")}
            </h3>
            <p className="mt-5 leading-relaxed text-slate-400">{t("home.warehouse.body")}</p>
            <Link
              to="/about"
              data-testid="trust-cta-about"
              className="group mt-8 inline-flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.2em] text-[#9db4ff] transition-colors hover:text-white"
            >
              {t("home.warehouse.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* OPERATIONS GALLERY SLIDER */}
      <OperationsSlider />

      {/* PRODUCTS BENTO GRID */}
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8" data-testid="home-products-grid">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
                {t("home.grid.kicker")}
              </p>
              <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
                {t("home.grid.title")}
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="grid-cta-all-products"
              className="group flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.2em] text-[#9db4ff] transition-colors hover:text-white"
            >
              {t("home.grid.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.slug} delay={0.08 * i} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <Link
                to={`/products?tab=${p.slug}`}
                data-testid={`home-product-card-${p.slug}`}
                className="group relative block overflow-hidden border border-white/10 bg-[#0d223f] transition-colors duration-500 hover:border-[#4a6cf7]/60"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d223f] via-transparent to-transparent" />
                  <span className="font-mono2 absolute left-4 top-4 border border-[#4a6cf7]/40 bg-[#0a192f]/70 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#9db4ff] backdrop-blur-sm">
                    {p.short}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-white transition-colors duration-300 group-hover:text-[#9db4ff]">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400">{p.tagline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <Link
              to="/contact"
              data-testid="home-product-card-rfq"
              className="group flex h-full min-h-[280px] flex-col items-start justify-between border border-[#4a6cf7]/50 bg-gradient-to-br from-[#4a6cf7]/15 to-transparent p-6 transition-colors duration-500 hover:border-[#4a6cf7]"
            >
              <p className="font-display text-xl font-semibold text-white">{t("home.grid.rfqCard")}</p>
              <span className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.2em] text-[#9db4ff] transition-colors group-hover:text-white">
                {t("home.grid.rfqCta")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="border-t border-[#4a6cf7]/15 bg-[#0d223f]" data-testid="lead-form-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
              {t("home.lead.kicker")}
            </p>
            <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t("home.lead.title")}
            </h2>
            <p className="mt-5 max-w-md leading-relaxed text-slate-400">{t("home.lead.body")}</p>
            <div className="mt-8 space-y-2 text-sm text-slate-400">
              <p><span className="text-slate-500">{t("home.lead.emailLabel")}</span> info@oresourcemining.com · sales@oresourcemining.com</p>
              <p><span className="text-slate-500">{t("home.lead.telLabel")}</span> +243 999 004 146</p>
              <p><span className="text-slate-500">{t("home.lead.officeLabel")}</span> 5967 AV Kasai, Q/Makutano C/Lubumbashi, Katanga, DR Congo</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            {leadRef ? (
              <div
                data-testid="lead-success-message"
                className="flex h-full flex-col items-start justify-center border border-[#4a6cf7]/50 bg-[#0a192f] p-10"
              >
                <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#9db4ff]">
                  {t("home.lead.successKicker")}
                </p>
                <p className="font-display mt-4 text-3xl font-bold text-white">{leadRef}</p>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("home.lead.successBody")}</p>
                <button
                  data-testid="lead-new-inquiry-btn"
                  onClick={() => setLeadRef(null)}
                  className="mt-8 border border-[#4a6cf7]/60 px-6 py-3 font-display text-xs font-semibold uppercase tracking-[0.2em] text-[#9db4ff] transition-colors hover:bg-[#4a6cf7] hover:text-white"
                >
                  {t("home.lead.newInquiry")}
                </button>
              </div>
            ) : (
              <form
                onSubmit={submitLead}
                data-testid="lead-capture-form"
                className="border border-white/10 bg-[#0a192f] p-8"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
                      {t("home.lead.company")}
                    </label>
                    <input
                      required
                      data-testid="lead-company-input"
                      value={lead.company_name}
                      onChange={(e) => setLead({ ...lead, company_name: e.target.value })}
                      className="mt-2 w-full border border-white/15 bg-[#0d223f] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#4a6cf7]"
                      placeholder={t("home.lead.companyPh")}
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
                      {t("home.lead.email")}
                    </label>
                    <input
                      required
                      type="email"
                      data-testid="lead-email-input"
                      value={lead.email}
                      onChange={(e) => setLead({ ...lead, email: e.target.value })}
                      className="mt-2 w-full border border-white/15 bg-[#0d223f] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#4a6cf7]"
                      placeholder={t("home.lead.emailPh")}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
                      {t("home.lead.commodity")}
                    </label>
                    <select
                      data-testid="lead-commodity-select"
                      value={lead.commodity}
                      onChange={(e) => setLead({ ...lead, commodity: e.target.value })}
                      className="mt-2 w-full border border-white/15 bg-[#0d223f] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#4a6cf7]"
                    >
                      <option value="">{t("home.lead.commodityPh")}</option>
                      {commodityOptions.map((c, i) => (
                        <option key={COMMODITIES[i]} value={COMMODITIES[i]}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
                      {t("home.lead.message")}
                    </label>
                    <textarea
                      rows={3}
                      data-testid="lead-message-input"
                      value={lead.message}
                      onChange={(e) => setLead({ ...lead, message: e.target.value })}
                      className="mt-2 w-full resize-none border border-white/15 bg-[#0d223f] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#4a6cf7]"
                      placeholder={t("home.lead.messagePh")}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="lead-submit-button"
                  className="mt-6 flex w-full items-center justify-center gap-3 bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6] px-6 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-white transition-transform duration-300 hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {sending ? t("home.lead.sending") : t("home.lead.submit")}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
